import {
  Box,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { MstProcFeeTireItem } from "features/mst_proc_fee/types/proc-fee";
import React, { useCallback, useMemo } from "react";
import TableCellComponent from "components/table/TableCellComponent";
import { formatCurrency } from "utils";
import TextFieldComponent from "components/input/TextFieldComponent";
import { TFieldType, TOption } from "type";
import {
  CASHBACK_PERIOD_OPTIONS,
  PUNCTURE_COMPENSATION_OPTIONS,
  ROUNDING_OPTIONS,
} from "features/mst_proc_fee/constants/options";
import { EstCreateRequest } from "features/car_model/types";

type CashbackKey = "cashback_period" | "puncture";
type SelectRowKey = CashbackKey | "round_type";
type EstResultKey = keyof EstCreateRequest;

type NumericEstResultKey =
  | "replac_labor_cost"
  | "waste_tire_cost"
  | "rubber_stem_valve_cost"
  | "nitrogen_inflation_cost"
  | "hub_rust_prevention_cost";

interface Props {
  setEstResultByKey?: (key: EstResultKey, value: any) => void;
  estResult: EstCreateRequest;
  estPrcChecked: Record<keyof MstProcFeeTireItem, boolean>;
  setEstPrcChecked: (key: keyof MstProcFeeTireItem, value: boolean) => void;
  estCashback: {
    cashback_period: number;
    puncture: string;
  };
  setEstCashback: (key: CashbackKey, value: any) => void;
}

interface EstResultDiscountHeader {
  key: EstResultKey | CashbackKey;
  label: string;
  hasCheckBox?: boolean;
  sx: SxProps;
}

interface EstResultDiscountSelectRow {
  key: SelectRowKey;
  options: TOption[];
}

interface EstResultDiscountPrcRow {
  key: NumericEstResultKey;
  formatter?: (value: number) => string;
  type?: TFieldType;
}

type EstResultDiscountRow = EstResultDiscountPrcRow | EstResultDiscountSelectRow;

const DISCOUNT_CHECKED_KEYS: EstResultKey[] = [
  "replac_labor_cost",
  "waste_tire_cost",
  "rubber_stem_valve_cost",
  "nitrogen_inflation_cost",
  "hub_rust_prevention_cost",
];

const SELECT_ROW_KEYS: SelectRowKey[] = ["cashback_period", "puncture", "round_type"];

const DISABLE_KEYS: CashbackKey[] = ["puncture", "cashback_period"];

const DISCOUNT_HEADER_LABEL: EstResultDiscountHeader[] = [
  {
    label: "交換工賃",
    key: "replac_labor_cost",
    sx: { width: "142px", whiteSpace: "nowrap" },
  },
  {
    label: "処分",
    key: "waste_tire_cost",
    sx: { width: "142px", whiteSpace: "nowrap" },
  },
  {
    label: "エアブルブ",
    key: "rubber_stem_valve_cost",
    sx: { width: "142px", whiteSpace: "nowrap" },
  },
  {
    label: "窒素",
    key: "nitrogen_inflation_cost",
    sx: { width: "142px", whiteSpace: "nowrap" },
  },
  {
    label: "ハブ防錆",
    key: "hub_rust_prevention_cost",
    sx: { width: "142px", whiteSpace: "nowrap" },
  },
  {
    label: "パンク補償",
    key: "tire_puncture_flag",
    sx: { width: "142px", whiteSpace: "nowrap" },
  },
  {
    label: "キャッシュバック",
    key: "cashback_period",
    sx: { width: "166px", whiteSpace: "nowrap" },
  },
  {
    label: "丸め",
    key: "round_type",
    sx: { width: "142px", whiteSpace: "nowrap" },
  },
];

const DISCOUNT_ROW_DATA: EstResultDiscountRow[] = [
  {
    key: "replac_labor_cost",
    formatter: formatCurrency,
    type: "input",
  },
  {
    key: "waste_tire_cost",
    formatter: formatCurrency,
    type: "input",
  },
  {
    key: "rubber_stem_valve_cost",
    formatter: formatCurrency,
    type: "input",
  },
  {
    key: "nitrogen_inflation_cost",
    formatter: formatCurrency,
    type: "input",
  },
  {
    key: "hub_rust_prevention_cost",
    formatter: formatCurrency,
    type: "input",
  },
  {
    key: "puncture",
    options: PUNCTURE_COMPENSATION_OPTIONS,
  },
  {
    key: "cashback_period",
    options: CASHBACK_PERIOD_OPTIONS,
  },
  {
    key: "round_type",
    options: ROUNDING_OPTIONS,
  },
];

const isCashbackRow = (row: EstResultDiscountRow): row is EstResultDiscountSelectRow =>
  SELECT_ROW_KEYS.includes(row.key as SelectRowKey);

const isCashbackKey = (key: string): key is CashbackKey =>
  DISABLE_KEYS.includes(key as CashbackKey);

/**
 * TableResultEstDiscount component renders a table with discount estimation results.
 */
export default function TableResultEstDiscount({
  estResult,
  setEstResultByKey,
  estPrcChecked,
  setEstPrcChecked,
  estCashback,
  setEstCashback,
}: Props) {
  const header = useMemo(
    () =>
      DISCOUNT_HEADER_LABEL.map((item) => ({
        label: item.label,
        sx: item.sx,
        key: item.key,
        hasCheckBox: DISCOUNT_CHECKED_KEYS.slice(1).includes(item.key as EstResultKey),
      })),
    [],
  );

  const getValue = useCallback(
    (key: SelectRowKey): number => {
      if (key === "puncture") {
        return estCashback.puncture ? Number(estCashback.puncture) : 0;
      }
      if (key === "cashback_period") {
        return estCashback.cashback_period ?? 0;
      }
      if (key === "round_type") {
        return estResult.round_type ? Number(estResult.round_type) : 0;
      }
      return 0;
    },
    [estCashback, estResult],
  );

  const setSelectValue = useCallback(
    (key: SelectRowKey, value: any): void => {
      if (isCashbackKey(key)) {
        setEstCashback(key, key === "puncture" ? String(value) : value);
        return;
      }
      if (key === "round_type") {
        setEstResultByKey?.(key, String(value));
      }
    },
    [setEstCashback, setEstResultByKey],
  );

  const shouldDisable = useCallback(
    (data: EstResultDiscountSelectRow): boolean => {
      const tireDetails = estResult.tbl_quote_detail_tire;
      if (tireDetails.length <= 0) return true;

      const firstQuantity = tireDetails[0]?.quantity;
      return [1, 2].includes(firstQuantity) && isCashbackKey(data.key);
    },
    [estResult.tbl_quote_detail_tire],
  );

  const handleCheckboxChange = useCallback(
    (key: keyof MstProcFeeTireItem) => {
      const currentValue = estPrcChecked[key] ?? false;
      setEstPrcChecked(key, !currentValue);
    },
    [estPrcChecked, setEstPrcChecked],
  );

  const renderCellContent = useCallback(
    (item: EstResultDiscountRow): React.ReactNode => {
      if (!isCashbackRow(item)) {
        const numericValue = estResult[item.key] as number;
        const formattedValue = formatCurrency(numericValue);
        if (item.type === "input") {
          const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            // Get value directly from DOM to handle select all + type scenario
            const inputValue = e.target.value;
            const extractedNumeric = inputValue.replace(/[^0-9]/g, "");
            if (setEstResultByKey) {
              // Set to undefined if empty to allow proper display of first character
              const newValue = extractedNumeric === "" ? undefined : Number(extractedNumeric);
              setEstResultByKey(item.key, newValue);
            }
          };

          // Convert to string, show empty if 0, undefined, or null
          const displayValue =
            numericValue !== undefined && numericValue !== null
              ? String(numericValue)
              : "0";

          return (
            <TextFieldComponent
              sx={{
                height: "100%",
                my: 0,
                "& .MuiInputBase-root": {
                  pt: "1px",
                  borderRadius: "0 !important",
                  "& fieldset": {
                    borderColor: "var(--silver-100)",
                  },
                },
              }}
              normalizeHalfWidth
              sxRoot={{ height: "100%", alignItems: "start", mt: "1px" }}
              sxInput={{
                padding: "0px",
                height: "100%",
                fontSize: { xs: "16px", md: "18px", xl: "20px" },
                textAlign: "center",
                py: { xs: "11.5px", md: "12.5px" },
                borderRadius: 0,
              }}
              value={displayValue}
              onInputText={handleInputChange}
              validateOption={{
                maxLength: { message: "", value: 10 },
              }}
            />
          );
        }

        return formattedValue ?? 0;
      }

      return (
        <FormControl fullWidth>
          <Select
            labelId={`select-${item.key}`}
            disabled={shouldDisable(item)}
            id={`select-${item.key}`}
            value={getValue(item.key)}
            label=""
            size="medium"
            sx={{
              boxSizing: "border-box",
              width: "100%",
              border: "none",
              borderRadius: "0px",
              fontSize: { xs: "16px", md: "18px", xl: "20px" },
              "& .MuiSelect-select": {
                padding: "8px 12px",
              },
              "& .MuiInputBase-input": {
                textAlign: "center",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
            onChange={(e) => setSelectValue(item.key, e.target.value)}
          >
            {item.options.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
    [estResult, setEstResultByKey, getValue, setSelectValue, shouldDisable],
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        minWidth: 1200,
        minHeight: 100,
      }}
    >
      <TableContainer component={Table} sx={{ boxShadow: "none", width: "auto" }}>
        <TableHead>
          <TableRow>
            {header.map((item) => (
              <TableCellComponent
                key={item.key}
                sx={{
                  ...item.sx,
                  backgroundColor:
                    (estPrcChecked[item.key as keyof MstProcFeeTireItem] ?? false)
                      ? "var(--green-600)"
                      : "var(--offwhite-500)",
                }}
                type="header"
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}
                >
                  {item.hasCheckBox && (
                    <Checkbox
                      checked={estPrcChecked[item.key as keyof MstProcFeeTireItem] ?? false}
                      onChange={() => handleCheckboxChange(item.key as keyof MstProcFeeTireItem)}
                      sx={{
                        "& .MuiSvgIcon-root": { fontSize: 28 },
                        padding: 0,
                      }}
                    />
                  )}
                  {item.label}
                </Box>
              </TableCellComponent>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {DISCOUNT_ROW_DATA.map((item) => (
              <TableCellComponent
                key={item.key}
                sx={{
                  ...(DISCOUNT_CHECKED_KEYS.includes(item.key as never) && {
                    p: "0 !important",
                    border: "none !important",
                  }),
                  ...(isCashbackRow(item) && { p: "0 !important" }),
                }}
              >
                {renderCellContent(item)}
              </TableCellComponent>
            ))}
          </TableRow>
        </TableBody>
      </TableContainer>
    </Box>
  );
}
